import Avatar from '@mui/joy/Avatar';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import Diversity1RoundedIcon from '@mui/icons-material/Diversity1Rounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';

import { useContext, useState } from 'react';
import { LayoutContext } from '../../context';
import { useNavigate } from 'react-router';
import { API_URL, APICreateUnion, APIGetChatInviteLink, APIGetMyUnions } from '../../api';
import BlockUserModal from './BlockUserModal';
import { Dropdown, Menu, MenuButton, MenuItem } from '@mui/joy';
import ModalCommunity from './ModalCommunity';

/**
 * Шапка переписки.
 *
 * Логика прежняя. Из оформления убраны ручные ширины заголовка по шести
 * брейкпоинтам (включая несуществующие sm2/xs2) — из-за них имя собеседника
 * на части экранов обрезалось до 10vw. Теперь обычное сокращение по ширине.
 */
export default function MessagesPaneHeader({ chat, setSelectedChat, searchParams, setSearchParams, wsConnected, onBack }) {
  const { token, currentUser } = useContext(LayoutContext);
  const navigate = useNavigate();
  const [isCreatingUnion, setIsCreatingUnion] = useState(false);
  const [blockUserModalOpen, setBlockUserModalOpen] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const handleGenerateInviteLink = async () => {
    if (!(chat?.id ?? chat?.chat_id)) return;

    setGeneratingLink(true);
    try {
      const response = await APIGetChatInviteLink(token, chat.id ?? chat.chat_id);
      await navigator.clipboard.writeText(response.invite_link);
      alert('Ссылка скопирована в буфер обмена!');
    } catch (error) {
      console.error('Ошибка генерации ссылки:', error);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCreateOrOpenUnion = async () => {
    if (!chat?.participants?.[0]) return;

    try {
      setIsCreatingUnion(true);

      const myUnionsResponse = await APIGetMyUnions(token);
      const myUnions = myUnionsResponse.unions || [];

      const existingUnion = myUnions.find(union =>
        union.members?.some(member => member.profile?.id === chat.participants[0].profile?.id)
      );

      if (existingUnion) {
        navigate(`/union/${existingUnion.id}`);
      } else {
        const unionData = {
          name: '',
          description: '',
          partner_profile_id: chat.participants[0].profile?.id,
        };

        const createResponse = await APICreateUnion(token, unionData);

        if (createResponse.success) {
          navigate(`/union/${createResponse.union_id}`);
        }
      }
    } catch (error) {
      console.error('Ошибка при работе с союзом:', error);
    } finally {
      setIsCreatingUnion(false);
    }
  };

  const handleBlockUser = () => {
    setBlockUserModalOpen(true);
  };

  const handleEditCommunity = () => {
    setEditingCommunity(chat);
  };

  // Редактировать и блокировать может только создатель или администратор
  const canEditCommunity = chat?.is_community && chat?.members?.some(member =>
    member.profile_data?.user_id === currentUser?.id &&
    (member.role === "creator" || member.role === "admin")
  );

  const canBlockUsers = chat?.is_community && chat?.members?.some(member =>
    member.profile_data?.user_id === currentUser?.id &&
    (member.role === "creator" || member.role === "admin")
  );

  const photo = chat.photo_url
    ? new URL(chat.photo_url, API_URL).href
    : (chat.is_community ? null : chat.participants?.[0]?.profile?.photo_file_path);

  return (
    <Stack
      direction="row"
      sx={{
        flex: 'none',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
        px: { xs: 1, sm: 2 },
        borderBottom: '1px solid',
        borderColor: 'var(--lvc-line)',
        backgroundColor: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          aria-label="Назад к списку чатов"
          sx={{ flex: 'none', minWidth: 44, minHeight: 44, borderRadius: '15px', color: '#9972ba', background: '#f5eefb', '& svg': { color: 'inherit' } }}
          onClick={() => {
            if (onBack) { onBack(); return; }
            setSelectedChat(null);
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("chatId");
            setSearchParams(newParams);
          }}
        >
          <ArrowBackIosNewRoundedIcon />
        </IconButton>

        <Avatar size="lg" src={photo} sx={{ flex: 'none', borderRadius: '17px', '--Avatar-size': { xs: '38px', sm: '44px' }, background: '#eee3fa', color: '#a174c0' }}>{(chat.display_name || chat.name || '?')[0]}</Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            level="title-sm"
            noWrap
            sx={{ fontWeight: 'lg', fontSize: 15.5, color: 'var(--lvc-ink)' }}
          >
            {chat.display_name || chat.name || 'Без названия'}
          </Typography>

          {chat.is_community ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
              <span className="lvc-hobby">
                <GroupsRoundedIcon sx={{ fontSize: 13, mr: 0.5 }} />
                {chat.member_count || 0}
              </span>
              {chat.city && (
                <span className="lvc-hobby">
                  <LocationOnRoundedIcon sx={{ fontSize: 13, mr: 0.5 }} />
                  {chat.city}
                </span>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.25 }}>
              <Box
                sx={{
                  width: 7, height: 7, borderRadius: '50%', flex: 'none',
                  backgroundColor: wsConnected ? '#22c55e' : '#d6cce4',
                }}
              />
              <Typography level="body-xs" sx={{ color: 'var(--lvc-ink-soft)' }}>
                {wsConnected ? 'Чат подключён' : 'Подключение к чату…'}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>

      <Stack spacing={1} direction="row" sx={{ alignItems: 'center', flex: 'none', '& button': { minHeight: 40 }, '& svg': { color: 'inherit' } }}>
        {chat?.is_community && (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleGenerateInviteLink}
            loading={generatingLink}
            aria-label="Ссылка-приглашение"
          >
            <ShareRoundedIcon />
          </IconButton>
        )}

        {/* Создание союза — только для личных чатов */}
        {!chat?.is_community && (
          <Button
            size="sm"
            variant="soft"
            color="primary"
            onClick={handleCreateOrOpenUnion}
            loading={isCreatingUnion}
            startDecorator={<Diversity1RoundedIcon sx={{ fontSize: 17, color: 'inherit' }} />}
            sx={{ borderRadius: '14px', px: { xs: 1, sm: 1.75 }, fontSize: 12, minWidth: 0 }}
          >
            Союз
          </Button>
        )}

        {(canEditCommunity || canBlockUsers) && (
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
              sx={{ borderRadius: 'md' }}
            >
              <MoreVertRoundedIcon />
            </MenuButton>
            <Menu placement="bottom-end" sx={{ zIndex: 10120, borderRadius: '16px' }} size="sm">
              <MenuItem onClick={handleEditCommunity}>
                <EditRoundedIcon sx={{ mr: 1 }} />
                Редактировать
              </MenuItem>
              <MenuItem onClick={handleBlockUser}>
                <BlockRoundedIcon sx={{ mr: 1 }} />
                Блокир. пользователя
              </MenuItem>
            </Menu>
          </Dropdown>
        )}
      </Stack>

      {/* Блокировка пользователя */}
      <BlockUserModal
        open={blockUserModalOpen}
        onClose={() => setBlockUserModalOpen(false)}
        chat={chat}
        token={token}
      />

      {/* Редактирование сообщества */}
      <ModalCommunity
        open={!!editingCommunity}
        onClose={() => setEditingCommunity(null)}
        onCommunityUpdated={(communityId, updatedCommunity) => {
          setSelectedChat(prev => prev ? { ...prev, ...updatedCommunity } : null);
          setEditingCommunity(null);
        }}
        communityId={editingCommunity?.chat_id || editingCommunity?.id}
        mode="edit"
      />
    </Stack>
  );
}
